import { Component, Input, OnInit } from '@angular/core';
import _ from 'lodash';
import {
  DashboardEngineiusKPIComponent,
  MetricDisplayStyle,
} from './dashboard-engineius-kpi.component';

enum ChartMetric {
  CompletedMoves,
  TargetMoves,
  LessThan1HourLate,
  OnTime,
  TotalActiveAccounts,
  MovementsMatched,
  MovementsCustomerMatched,
  AverageWeeklyMovements,
}

@Component({
  selector: 'dashboard-engineius-commercial-kpi',
  styleUrls: ['./dashboard-engineius-kpi.component.scss'],
  templateUrl: './dashboard-engineius-kpi.component.html',
})
export class DashboardEngineiusKPICommercialComponent extends DashboardEngineiusKPIComponent {
  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    console.log('Commercial INIT');
    this.selectedMonthRange = 12;

    // load chart data
    await this.loadData();
  }

  async loadData() {
    console.log('Loading Commercial chart data');

    await super.loadData();

    this.monthlyTotals = await this.kpiService.getKPICommercialTotals(
      this.kpiStartDate,
      this.selectedMonthRange,
    );

    this.setMonthDates(this.selectedMonthRange);

    this.completedTotals = this.monthlyTotals.filter((x) => !x.isCancelled);
    //this.cancelledTotals = this.monthlyTotals.filter(x => x.isCancelled);

    //console.log("Commercial totals: ", this.completedTotals);

    this.totalsChartData = [];

    const dataMap = new Map<number, number[]>([
      [
        ChartMetric.CompletedMoves,
        this.completedTotals.map((x) => {
          return x.totalCompleted;
        }),
      ],
      [
        ChartMetric.TargetMoves,
        this.completedTotals.map((x) => {
          return x.targetCompleted;
        }),
      ],
      [
        ChartMetric.LessThan1HourLate,
        this.completedTotals.map((x) => {
          return x.lessThan1HourLate;
        }),
      ],
      [
        ChartMetric.OnTime,
        this.completedTotals.map((x) => {
          return x.onTime;
        }),
      ],
      [
        ChartMetric.TotalActiveAccounts,
        this.completedTotals.map((x) => {
          return x.totalActiveAccounts;
        }),
      ],
      [
        ChartMetric.MovementsMatched,
        this.completedTotals.map((x) => {
          return x.movementsMatched;
        }),
      ],
      [
        ChartMetric.MovementsCustomerMatched,
        this.completedTotals.map((x) => {
          return x.movementsCustomerMatched;
        }),
      ],
      [
        ChartMetric.AverageWeeklyMovements,
        this.completedTotals.map((x) => {
          return x.averageWeeklyMovements;
        }),
      ],
    ]);

    this.totalsChartData = Array.from(dataMap.values());
    //console.log("Commercial Totals chart data: ", this.totalsChartData);

    this.targetDataIndex = <number>ChartMetric.TargetMoves;

    // create metric card data for each kpi measure
    this.monthlyMetrics = [];
    this.createChart_TotalActiveAccounts();
    this.createChart_MovesMatched();

    this.isLoading = false;

    console.log('Commercial Metrics: ', this.monthlyMetrics);
  }

  createChart_TotalActiveAccounts() {
    let metrics = [];

    let metric = this.getMetricData_v2(
      'Total Active Accounts',
      ChartMetric.TotalActiveAccounts,
      false,
      false,
      false,
      MetricDisplayStyle.Number,
    );
    let chartData = this.getSingleChartData(
      ChartMetric.TotalActiveAccounts,
      'rgba(10, 220, 85, 0.5)',
      'Active',
    );
    metric.chartData = chartData;
    metric.noComp = true;
    metric.tooltip =
      'Customer organisations where one or more orders were placed in the last 90 days';

    //console.log("Active accounts chart data: ", chartData);

    //metric.chartData.options = this.baseChartOptions;
    metric.totalName = 'Active';

    metrics.push(metric);
    this.monthlyMetrics.push({ groupName: 'Total Active Accounts', metrics });
  }

  createChart_MovesMatched() {
    let metrics = [];

    let compColor = 'rgba(66, 164, 245, 0.7)';
    let moveEngColor = 'rgba(10, 220, 185, 0.7)';
    let moveCustColor = 'rgba(10, 220, 85, 0.7)';

    const options = _.cloneDeep(this.twoSetChartOptions);
    options.scales.yAxes[1].ticks.callback = function (value, index, values) {
      return value + '%';
    };
    //options.scales.yAxes[0].ticks.fontColor = compColor;

    let metric = this.getMetricData_v2(
      '% Movements Matched By Customer',
      ChartMetric.MovementsCustomerMatched,
      false,
      true,
      false,
      MetricDisplayStyle.Percentage,
    );

    // let chartData = this.getTwoSetChartData(ChartMetric.MovementsCustomerMatched, ChartMetric.CompletedMoves, moveCustColor, compColor,
    //   "Moves Customer Matched", "CompletedMoves", null, null, 'bar', 'line', null, true);
    let chartData = this.getTwoSetChartData(
      ChartMetric.MovementsCustomerMatched,
      ChartMetric.CompletedMoves,
      moveCustColor,
      compColor,
      'Matched By Customer',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      null,
      true,
    );

    //
    // let chartData = this.getStackedChartData(ChartMetric.MovementsCustomerMatched, ChartMetric.CompletedMoves
    //   , moveCustColor, compColor,"% Moves Customer Matched", "Completed Moves", "bar", false);

    metric.chartData = chartData;
    //options.scales.yAxes[1].ticks.fontColor = moveCustColor;
    metric.chartData.options = options;
    metric.noComp = true;
    metric.totalName = 'Matched By Customer';
    metric.useValueAsPercent = true;
    metric.tooltip =
      'Tasks combined with one or more other tasks by the customer as a percentage of total tasks';
    metrics.push(metric);

    metric = this.getMetricData_v2(
      '% Movements Matched By Engineius',
      ChartMetric.MovementsMatched,
      false,
      true,
      false,
      MetricDisplayStyle.Percentage,
    );
    //chartData = this.getPercentageChartData(ChartMetric.MovementsMatched, "% Movements Engineius Matched", moveEngColor, 'line');

    //chartData = this.getStackedChartData(ChartMetric.MovementsMatched, ChartMetric.CompletedMoves
    //      , moveEngColor, compColor,"% Moves Engineius Matched", "Completed Moves", "bar", false);

    chartData = this.getTwoSetChartData(
      ChartMetric.MovementsMatched,
      ChartMetric.CompletedMoves,
      moveEngColor,
      compColor,
      'Matched By Engineius',
      'CompletedMoves',
      null,
      null,
      'bar',
      'line',
      null,
      true,
    );

    metric.chartData = chartData;
    metric.noComp = true;
    //options.scales.yAxes[1].ticks.fontColor = moveEngColor;
    metric.chartData.options = options;
    metric.totalName = 'Matched by Engineius';
    metric.useValueAsPercent = true;
    metric.tooltip =
      'Tasks combined with one or more other tasks in a job before initial supplier dispatch as a percentage of total tasks';

    metrics.push(metric);

    this.monthlyMetrics.push({ groupName: 'Movements Matched', metrics });
  }
}
